// Centralized date DISPLAY helpers (#4204).
//
// These are the ONE place allowed to turn a DateTime into a human-readable date
// string. Every display site calls a helper here instead of Luxon directly, and
// the formatting-boundary guard (scripts/ci/check-formatting-boundaries.mjs)
// enforces that. Keeping it centralized is why the whole app localizes from a
// single, testable seam.
//
// The month/weekday NAMES and the field ORDER both follow the UI *language*
// passed as `locale` ('en' | 'es' | 'nl' | 'fr'). We type `locale` as `string`
// because shared code cannot import the client's generated `Locales` type; the
// client always passes its `BOOT_LOCALE`, and tests pass the locale directly.
//
// The locale is passed EXPLICITLY (never read from a hidden global), which
// mirrors how the number formatters take an explicit `LocaleSettings`. See
// design-docs/date-time-localization.md.
//
// Machine / stable-id / log formatting does NOT belong here — it must stay
// locale-independent. Use shared/src/date-format-machine.ts for that.

import { DateTime, DateTimeFormatOptions } from 'luxon';
import { DateFormat } from './enums';

/**
 * The low-level display escape hatch: apply the UI-language `locale` and format
 * with a raw Intl option bag, so both names and order follow that locale. Prefer
 * one of the named wrappers below; use this only for a genuinely one-off bag.
 * Named `…WithOptions` (not just `formatDate`) so it does not collide with the
 * page-level `formatDate` methods some components already define.
 */
export function formatDateWithOptions(dt: DateTime, locale: string, options: DateTimeFormatOptions): string {
  return dt.setLocale(locale).toLocaleString(options);
}

/** Full weekday name, e.g. "Friday" / "viernes". */
export function formatWeekdayLong(dt: DateTime, locale: string): string {
  return formatDateWithOptions(dt, locale, { weekday: 'long' });
}

/** Abbreviated weekday name, e.g. "Fri" / "vie". */
export function formatWeekdayShort(dt: DateTime, locale: string): string {
  return formatDateWithOptions(dt, locale, { weekday: 'short' });
}

/**
 * Single-letter weekday name for a compact date strip, e.g. "F" / "V".
 *
 * Note that "narrow" is NOT unique per weekday in most languages — English
 * repeats "T" for Tuesday and Thursday, and Spanish repeats "M" for martes and
 * miércoles. That is the correct, expected rendering for a calendar strip,
 * where the column position disambiguates. Do not use this anywhere the letter
 * has to stand on its own.
 */
export function formatWeekdayNarrow(dt: DateTime, locale: string): string {
  return formatDateWithOptions(dt, locale, { weekday: 'narrow' });
}

/** Full month name, e.g. "October" / "octubre". */
export function formatMonthLong(dt: DateTime, locale: string): string {
  return formatDateWithOptions(dt, locale, { month: 'long' });
}

/** Abbreviated month name, e.g. "Oct" / "oct". */
export function formatMonthShort(dt: DateTime, locale: string): string {
  return formatDateWithOptions(dt, locale, { month: 'short' });
}

/** Full month name + year, e.g. "October 2025" / "octubre de 2025". */
export function formatMonthYearLong(dt: DateTime, locale: string): string {
  return formatDateWithOptions(dt, locale, { month: 'long', year: 'numeric' });
}

/** Abbreviated month + day, order follows locale, e.g. "Oct 7" / "7 oct". */
export function formatDayMonthShort(dt: DateTime, locale: string): string {
  return formatDateWithOptions(dt, locale, { month: 'short', day: 'numeric' });
}

/** Full weekday + full month + day (no year), e.g. "Friday, October 6" / "viernes, 6 de octubre". */
export function formatWeekdayMonthDayLong(dt: DateTime, locale: string): string {
  return formatDateWithOptions(dt, locale, { weekday: 'long', month: 'long', day: 'numeric' });
}

/** Abbreviated weekday + month + day (no year), e.g. "Sat, Oct 6" / "sáb, 6 oct". */
export function formatWeekdayDayMonthShort(dt: DateTime, locale: string): string {
  return formatDateWithOptions(dt, locale, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Abbreviated weekday + month + 2-digit day (no year), e.g. "Mon, Apr 19" / "lun, 19 abr". */
export function formatWeekdayDatePadded(dt: DateTime, locale: string): string {
  return formatDateWithOptions(dt, locale, { weekday: 'short', month: 'short', day: '2-digit' });
}

/** Abbreviated weekday + medium date with 2-digit day, e.g. "Sun, Apr 17, 2022" / "dom, 17 abr 2022". */
export function formatWeekdayDatePaddedWithYear(dt: DateTime, locale: string): string {
  return formatDateWithOptions(dt, locale, { weekday: 'short', year: 'numeric', month: 'short', day: '2-digit' });
}

/** Medium date, order follows locale, e.g. "Mar 5, 2026" / "5 mar 2026". */
export function formatDateMed(dt: DateTime, locale: string): string {
  return formatDateWithOptions(dt, locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Medium date with a 2-digit day, e.g. "Mar 05, 2026" / "05 mar 2026". */
export function formatDateMedPadded(dt: DateTime, locale: string): string {
  return formatDateWithOptions(dt, locale, { year: 'numeric', month: 'short', day: '2-digit' });
}

/** Abbreviated weekday + medium date, e.g. "Friday, Oct 6, 2025" / "viernes, 6 oct 2025". */
export function formatWeekdayDateMed(dt: DateTime, locale: string): string {
  return formatDateWithOptions(dt, locale, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
}

// --- the two override-aware cores (numeric short date + time-of-day) ---
// These live here (rather than in the client, which wraps them with a
// SettingsService) so they are pure and unit-testable with explicit inputs.

/**
 * Numeric short date. The explicit `dateFormat` override (US/UK/EUDot) wins and
 * produces its exact numeric field order (a language-neutral Luxon token); only
 * the `Automatic` branch follows the UI language for the order. This is the ONE
 * date presentation the dateFormat override reaches — named/compound dates
 * always follow the locale (#4204).
 */
export function formatShortDateCore(date: DateTime, dateFormat: DateFormat, locale: string): string {
  if (dateFormat === DateFormat.Automatic) {
    return formatDateWithOptions(date, locale, { year: '2-digit', month: 'numeric', day: 'numeric' });
  }
  return date.toFormat(dateFormat);
}

/**
 * Time of day. The 12/24-hour choice is passed in (it comes from the user's
 * clockHourMode); the UI language drives the AM/PM wording. #4204.
 */
export function formatTimeCore(time: DateTime, is24hr: boolean, locale: string): string {
  const format: DateTimeFormatOptions = is24hr
    ? { hour: 'numeric', minute: '2-digit', hour12: false }
    : { hour: '2-digit', minute: '2-digit', hour12: true };
  return time.setLocale(locale).toLocaleString(format);
}
